import { useQuery } from '@tanstack/react-query'
import { customersApi } from '@/lib/customers-api'
import type { CustomerListItem, CustomerDetail } from '@/lib/customer-types'

export interface CustomersListResult {
  customers: CustomerListItem[]
  /** False when the product bridge is unreachable (customers will be empty). */
  available: boolean
}

/**
 * The customer list, proxied from the TMAgent product API via the runtime
 * bridge. `source: 'unavailable'` (empty list) surfaces here as
 * `available: false` so the page shows a "bridge unavailable" state rather than
 * an empty "no customers" table.
 */
export function useCustomers() {
  return useQuery<CustomersListResult>({
    queryKey: ['customers'],
    queryFn: async () => {
      const res = await customersApi.list()
      return {
        customers: Array.isArray(res.data) ? res.data : [],
        available: res.source !== 'unavailable',
      }
    },
    staleTime: 30_000,
  })
}

export interface CustomerDetailResult {
  customer: CustomerDetail | null
  /** False when the customer is unknown OR the product bridge is unreachable. */
  available: boolean
}

/**
 * One customer's full record. `tenantId` is optional so the hook can be called
 * unconditionally from a routed page; the query stays disabled until it's set.
 */
export function useCustomer(tenantId: string | undefined) {
  return useQuery<CustomerDetailResult>({
    queryKey: ['customer', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const res = await customersApi.detail(tenantId as string)
      return {
        customer: res.data ?? null,
        available: res.source !== 'unavailable',
      }
    },
    staleTime: 30_000,
  })
}
